import Service, { type Registry as Services, service } from '@ember/service';
import type {
  CostProfileData,
  KpiData,
  NetRevenueCostProfile,
  NetRevenueInputs,
  NetRevenuePeriod,
  NetRevenuePeriods,
  NetRevenuePeriodsArgs,
  NetRevenueRemaining,
  NetRevenueStayDatePoint,
  StayDateData,
} from 'lumen/utils/net-revenue/types';

const MOCK_COMPARISON_LABEL = 'vs. Last year';

/*
 * Canonical fixture range: 91 days (Apr 1 – Jun 30 2026) with a SWLY
 * comparison period 52 weeks earlier, omitting one day to demonstrate
 * the "no comparable data" rendering.
 */
const CURRENT_RANGE_START = '2026-04-01';
const COMPARISON_RANGE_START = '2025-04-02';
const RANGE_LENGTH_IN_DAYS = 91;
const COMPARISON_NO_DATA_DATE = '2025-05-07';

/*
 * Comparison stay-date volumes run ~72% of the current period so the
 * comparison reads as naturally smaller. (Ratio preserved from the
 * original fixture.)
 */
const COMPARISON_VOLUME_MULTIPLIER = 379 / 530;

const CURRENT_COST_PROFILES_BASE: readonly NetRevenueCostProfile[] = [
  { name: 'Booking.com', roomNights: 150, roomRevenue: 24000, cost: 2880 },
  { name: 'Expedia', roomNights: 100, roomRevenue: 17000, cost: 2210 },
  { name: 'Direct', roomNights: 240, roomRevenue: 36000, cost: 540 },
  { name: 'Corporate', roomNights: 40, roomRevenue: 5800, cost: 348 },
  { name: 'Hotels.com', roomNights: 80, roomRevenue: 14000, cost: 2100 },
  { name: 'Airbnb', roomNights: 25, roomRevenue: 5000, cost: 900 },
  { name: 'GDS', roomNights: 60, roomRevenue: 9900, cost: 1188 },
  { name: 'Travel Agent', roomNights: 35, roomRevenue: 5425, cost: 326 },
  { name: 'Wholesaler', roomNights: 45, roomRevenue: 6300, cost: 1134 },
  { name: 'Google Hotel Ads', roomNights: 90, roomRevenue: 14220, cost: 1706 },
];

/*
 * 'Direct' and 'Travel Agent' are deliberately absent — those channels have
 * no comparable data in the comparison period, triggering the per-row
 * em-dash rendering.
 */
const COMPARISON_COST_PROFILES_BASE: readonly NetRevenueCostProfile[] = [
  { name: 'Booking.com', roomNights: 135, roomRevenue: 20925, cost: 2511 },
  { name: 'Expedia', roomNights: 88, roomRevenue: 14520, cost: 1888 },
  { name: 'Corporate', roomNights: 36, roomRevenue: 5112, cost: 307 },
  { name: 'Hotels.com', roomNights: 95, roomRevenue: 15960, cost: 2713 },
  { name: 'Airbnb', roomNights: 20, roomRevenue: 3800, cost: 608 },
  { name: 'GDS', roomNights: 55, roomRevenue: 8800, cost: 968 },
  { name: 'Wholesaler', roomNights: 70, roomRevenue: 10150, cost: 1421 },
  { name: 'Google Hotel Ads', roomNights: 85, roomRevenue: 12920, cost: 1680 },
];

const REMAINING_RESERVATIONS_BASE: NetRevenueRemaining = {
  roomNights: 10,
  roomRevenue: 1600,
};

/** Plain ISO-date arithmetic so this fixture has no dayjs dependency. */
function addDaysToIsoDate(isoDate: string, daysToAdd: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + daysToAdd);
  return date.toISOString().slice(0, 10);
}

/**
 * Build a deterministic stay-date series. Weekdays sell better than
 * weekends, ADR sits in the $55–$66 band, distribution cost is 10–13%
 * of room revenue. `dayOffset`-based seeding keeps the fixture stable
 * across page reloads, lints, and tests. `volumeMultiplier` scales every
 * per-day primitive so the comparison period can be naturally smaller
 * than the current one.
 */
function generateStayDateSeries(
  startDate: string,
  numberOfDays: number,
  skipDate: string | undefined,
  volumeMultiplier: number,
): NetRevenueStayDatePoint[] {
  const points: NetRevenueStayDatePoint[] = [];
  for (let dayOffset = 0; dayOffset < numberOfDays; dayOffset += 1) {
    const date = addDaysToIsoDate(startDate, dayOffset);
    if (date === skipDate) {
      continue;
    }
    const dayOfWeek = new Date(`${date}T00:00:00Z`).getUTCDay();
    const isWeekend = dayOfWeek === 5 || dayOfWeek === 6;
    const baseOccupancy = isWeekend ? 0.62 : 0.84;
    const occupancyJitter = ((dayOffset * 17) % 11) / 100; // 0.00 – 0.10
    const occupancy = Math.min(0.98, baseOccupancy + occupancyJitter);
    const roomNights = Math.round(occupancy * 100 * volumeMultiplier);

    const averageDailyRate = 55 + ((dayOffset * 7) % 12); // 55 – 66
    const roomRevenue = roomNights * averageDailyRate;

    const costPercent = 0.1 + ((dayOffset * 3) % 4) / 100; // 0.10 – 0.13
    const cost = Math.round(roomRevenue * costPercent);

    points.push({ capacity: 100, cost, date, roomNights, roomRevenue });
  }
  return points;
}

/** Sum a stay-date series into a single `NetRevenueInputs` for totals. */
function sumStayDateSeries(
  points: NetRevenueStayDatePoint[],
): NetRevenueInputs {
  return points.reduce(
    (running, point) => ({
      cost: running.cost + point.cost,
      roomNights: running.roomNights + point.roomNights,
      roomRevenue: running.roomRevenue + point.roomRevenue,
    }),
    { cost: 0, roomNights: 0, roomRevenue: 0 },
  );
}

interface DimensionScale {
  cost: number;
  roomNights: number;
  roomRevenue: number;
}

/** Sum base rows per dimension; a missing `cost` (e.g. remaining) counts as 0. */
function aggregateBase(
  rows: readonly { cost?: number; roomNights: number; roomRevenue: number }[],
): NetRevenueInputs {
  return rows.reduce<NetRevenueInputs>(
    (running, row) => ({
      cost: running.cost + (row.cost ?? 0),
      roomNights: running.roomNights + row.roomNights,
      roomRevenue: running.roomRevenue + row.roomRevenue,
    }),
    { cost: 0, roomNights: 0, roomRevenue: 0 },
  );
}

/**
 * Per-dimension factor that rescales a base aggregate so the scaled
 * cost-profile rows (plus remaining reservations) sum to the period's
 * stay-date totals on every dimension — keeping the table footer in
 * agreement with the visible row sums.
 */
function dimensionScale(
  target: NetRevenueInputs,
  base: NetRevenueInputs,
): DimensionScale {
  return {
    cost: base.cost === 0 ? 0 : target.cost / base.cost,
    roomNights: base.roomNights === 0 ? 0 : target.roomNights / base.roomNights,
    roomRevenue:
      base.roomRevenue === 0 ? 0 : target.roomRevenue / base.roomRevenue,
  };
}

/** Scale a cost-profile row by the per-dimension `scale`. */
function scaleCostProfile(
  profile: NetRevenueCostProfile,
  scale: DimensionScale,
): NetRevenueCostProfile {
  return {
    cost: Math.round(profile.cost * scale.cost),
    name: profile.name,
    roomNights: Math.round(profile.roomNights * scale.roomNights),
    roomRevenue: Math.round(profile.roomRevenue * scale.roomRevenue),
  };
}

/** Scale a remaining-reservations bucket by the per-dimension `scale`. */
function scaleRemainingReservations(
  reservations: NetRevenueRemaining,
  scale: DimensionScale,
): NetRevenueRemaining {
  return {
    roomNights: Math.round(reservations.roomNights * scale.roomNights),
    roomRevenue: Math.round(reservations.roomRevenue * scale.roomRevenue),
  };
}

const currentStayDateSeries = generateStayDateSeries(
  CURRENT_RANGE_START,
  RANGE_LENGTH_IN_DAYS,
  undefined,
  1,
);
const comparisonStayDateSeries = generateStayDateSeries(
  COMPARISON_RANGE_START,
  RANGE_LENGTH_IN_DAYS,
  COMPARISON_NO_DATA_DATE,
  COMPARISON_VOLUME_MULTIPLIER,
);

const currentTotals = sumStayDateSeries(currentStayDateSeries);
const comparisonTotals = sumStayDateSeries(comparisonStayDateSeries);

/*
 * Each period's rows are scaled to its own stay-date totals, per dimension.
 * The current base includes remaining reservations (current shows that row),
 * so the scaled profiles + remaining sum back to the footer; comparison has
 * no remaining row, so its base is the profiles alone.
 */
const currentScale = dimensionScale(
  currentTotals,
  aggregateBase([...CURRENT_COST_PROFILES_BASE, REMAINING_RESERVATIONS_BASE]),
);
const comparisonScale = dimensionScale(
  comparisonTotals,
  aggregateBase(COMPARISON_COST_PROFILES_BASE),
);

/**
 * Source-of-truth fixture for the Net Revenue dashboard. Matches the
 * proposed REST API response one-for-one: `{ roomNights, roomRevenue, cost }`
 * per row, plus `capacity` per stay-date point and the cost-profile `name`.
 * Every other metric shown in the UI is derived from these fields by
 * `lumen/utils/net-revenue/derive`.
 *
 * When the BE endpoint ships, this fixture stays in place for tests and
 * the service swaps to `fetch()` — the shape is identical.
 */
export const MOCK_PERIODS: NetRevenuePeriods = {
  comparison: {
    costProfiles: COMPARISON_COST_PROFILES_BASE.map((profile) =>
      scaleCostProfile(profile, comparisonScale),
    ),
    remainingReservations: null,
    stayDate: comparisonStayDateSeries,
    totals: comparisonTotals,
  },
  current: {
    costProfiles: CURRENT_COST_PROFILES_BASE.map((profile) =>
      scaleCostProfile(profile, currentScale),
    ),
    remainingReservations: scaleRemainingReservations(
      REMAINING_RESERVATIONS_BASE,
      currentScale,
    ),
    stayDate: currentStayDateSeries,
    totals: currentTotals,
  },
};

/** Inclusive whole-day delta between two ISO `yyyy-mm-dd` strings. */
function daysBetween(fromDate: string, toDate: string): number {
  const start = new Date(`${fromDate}T00:00:00Z`).getTime();
  const end = new Date(`${toDate}T00:00:00Z`).getTime();
  return Math.round((end - start) / (1000 * 60 * 60 * 24));
}

/**
 * Compute the comparison-period's end date by mirroring the current
 * period's length forward from `comparisonFromDate`.
 */
function computeComparisonToDate(
  currentFromDate: string,
  currentToDate: string,
  comparisonFromDate: string,
): string {
  return addDaysToIsoDate(
    comparisonFromDate,
    daysBetween(currentFromDate, currentToDate),
  );
}

/**
 * Trim a full-period fixture down to the inclusive `[fromDate, toDate]`
 * window: filter `stayDate`, re-sum `totals`, and proportionally scale
 * `costProfiles` + `remainingReservations` so the table footer agrees
 * with the visible rows inside the slice.
 */
function buildPeriodSlice(
  fullPeriod: NetRevenuePeriod,
  fromDate: string,
  toDate: string,
): NetRevenuePeriod {
  const stayDate = fullPeriod.stayDate.filter(
    (point) => point.date >= fromDate && point.date <= toDate,
  );
  const totals = sumStayDateSeries(stayDate);
  // The full period's rows already sum to its totals, so scaling them by the
  // slice-to-full ratio (per dimension) keeps the slice footer in agreement
  // with its visible rows.
  const sliceScale = dimensionScale(totals, fullPeriod.totals);
  return {
    costProfiles: fullPeriod.costProfiles.map((profile) =>
      scaleCostProfile(profile, sliceScale),
    ),
    remainingReservations: fullPeriod.remainingReservations
      ? scaleRemainingReservations(fullPeriod.remainingReservations, sliceScale)
      : null,
    stayDate,
    totals,
  };
}

/**
 * Slice the canonical {@link MOCK_PERIODS} fixture down to the window
 * requested by the service caller. Mirrors what the real BE endpoint
 * will do once it ships — same response shape, just sourced from
 * `fetch()` instead of an in-memory fixture.
 *
 * If the comparison-period slice ends up empty (e.g. the user picked
 * a range that pre-dates the fixture), `comparison` is returned as
 * `null` so the dashboard renders the "no comparison" UI cleanly
 * instead of an all-zero variance column.
 */
export function buildMockPeriodsSlice(
  args: NetRevenuePeriodsArgs,
): NetRevenuePeriods {
  const current = buildPeriodSlice(
    MOCK_PERIODS.current,
    args.fromDate,
    args.toDate,
  );

  let comparison: NetRevenuePeriod | null = null;
  if (MOCK_PERIODS.comparison && args.comparisonFromDate) {
    const comparisonToDate = computeComparisonToDate(
      args.fromDate,
      args.toDate,
      args.comparisonFromDate,
    );
    const sliced = buildPeriodSlice(
      MOCK_PERIODS.comparison,
      args.comparisonFromDate,
      comparisonToDate,
    );
    if (sliced.stayDate.length > 0) {
      comparison = sliced;
    }
  }

  return { comparison, current };
}

export default class NetRevenueMockDataService extends Service {
  @service('net-revenue/derive') declare derive: Services['net-revenue/derive'];

  buildMockCostProfile(): CostProfileData {
    return this.derive.deriveCostProfile(MOCK_PERIODS, MOCK_COMPARISON_LABEL)!;
  }

  buildMockKpi(): KpiData {
    return this.derive.deriveKpi(MOCK_PERIODS)!;
  }

  buildMockStayDate(): StayDateData {
    return this.derive.deriveStayDate(MOCK_PERIODS, MOCK_COMPARISON_LABEL)!;
  }
}

declare module '@ember/service' {
  interface Registry {
    'net-revenue/mock-data': NetRevenueMockDataService;
  }
}
